import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { astrologyService } from '../services/astrologyService';
import './Compatibility.css';

const Compatibility: React.FC = () => {
  const [yourName, setYourName] = useState('');
  const [yourSign, setYourSign] = useState('');
  const [yourBirthDate, setYourBirthDate] = useState('');
  const [yourBirthTime, setYourBirthTime] = useState('');
  const [yourBirthCity, setYourBirthCity] = useState('');
  
  const [partnerName, setPartnerName] = useState('');
  const [partnerSign, setPartnerSign] = useState('');
  const [partnerBirthDate, setPartnerBirthDate] = useState('');
  const [partnerBirthTime, setPartnerBirthTime] = useState('');
  const [partnerBirthCity, setPartnerBirthCity] = useState('');
  
  const [relationshipType, setRelationshipType] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [compatibilityResult, setCompatibilityResult] = useState<any>(null);
  const [cities, setCities] = useState<string[]>([]);
  const [useAdvancedMode, setUseAdvancedMode] = useState(false);

  const zodiacSigns = [
    'Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo',
    'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'
  ];

  const relationshipTypes = [
    'Love & Romance',
    'Marriage',
    'Friendship',
    'Business Partnership',
    'Family Relationship',
    'Dating',
    'Long-term Relationship',
    'Casual Relationship',
    'Professional Colleagues',
    'Creative Partnership',
    'Mentor-Mentee',
    'Sibling Bond',
    'Parent-Child',
    'Team Members'
  ];

  useEffect(() => {
    const loadCities = async () => {
      try {
        const data = await astrologyService.getCities();
        if (data.status === 'success') {
          setCities(data.cities);
        }
      } catch (err) {
        console.error('Failed to load cities:', err);
        setCities([
          "Delhi", "Mumbai", "Bangalore", "Hyderabad", "Chennai", "Kolkata", "Pune", "Ahmedabad",
          "Jaipur", "Lucknow", "Kochi", "Visakhapatnam", "Indore", "Chandigarh", "Nagpur", "Vadodara",
          "Aurangabad", "Coimbatore", "Trivandrum", "Ludhiana", "Varanasi", "Kanpur", "Prayagraj", "Patna",
          "Ranchi", "Bhopal", "Raipur", "Guwahati", "Ghaziabad", "Noida", "Gurugram", "Faridabad"
        ]);
      }
    };

    loadCities();
  }, []);

  const handleCheckCompatibility = async () => {
    if (!yourSign || !partnerSign || !relationshipType) {
      setError('Please fill in all details');
      return;
    }

    if (useAdvancedMode && (!yourName || !partnerName || !yourBirthDate || !partnerBirthDate || !yourBirthCity || !partnerBirthCity)) {
      setError('Please fill in all advanced details');
      return;
    }

    setLoading(true);
    setError('');
    setCompatibilityResult(null);

    try {
      const score = getCompatibilityScore(yourSign, partnerSign);
      const elements: Record<string, string> = {
        'Aries': 'Fire', 'Leo': 'Fire', 'Sagittarius': 'Fire',
        'Taurus': 'Earth', 'Virgo': 'Earth', 'Capricorn': 'Earth',
        'Gemini': 'Air', 'Libra': 'Air', 'Aquarius': 'Air',
        'Cancer': 'Water', 'Scorpio': 'Water', 'Pisces': 'Water'
      };
      
      const element1 = elements[yourSign];
      const element2 = elements[partnerSign];
      
      let analysis = `${yourSign} (${element1}) and ${partnerSign} (${element2}) have a compatibility score of ${score}/100. `;
      
      if (element1 === element2) {
        analysis += `You share the same ${element1} element, creating natural understanding and similar approaches to life.`;
      } else if ((element1 === 'Fire' && element2 === 'Air') || (element1 === 'Air' && element2 === 'Fire')) {
        analysis += `Fire and Air signs create an exciting, dynamic partnership full of energy and ideas.`;
      } else if ((element1 === 'Earth' && element2 === 'Water') || (element1 === 'Water' && element2 === 'Earth')) {
        analysis += `Earth and Water signs form a nurturing, stable bond with deep emotional connection.`;
      } else if ((element1 === 'Fire' && element2 === 'Water') || (element1 === 'Water' && element2 === 'Fire')) {
        analysis += `Fire and Water signs create passionate but challenging relationships requiring balance.`;
      } else {
        analysis += `Your different elements bring unique perspectives that can complement each other with understanding.`;
      }

      const strengths = score >= 85 
        ? `Strong natural harmony between ${yourSign} and ${partnerSign}. Your energies complement each other beautifully.`
        : score >= 70
        ? `Good potential for harmony with some understanding and compromise.`
        : `Different approaches that require patience and communication to find common ground.`;

      const challenges = score >= 85
        ? `Maintaining individual identity while enjoying your strong connection.`
        : score >= 70
        ? `Navigating differences in communication styles and emotional needs.`
        : `Overcoming fundamental differences in approach and perspective.`;

      let advice = '';
      if (relationshipType.includes('Love') || relationshipType.includes('Romance') || relationshipType.includes('Dating')) {
        advice = score >= 85
          ? `Your cosmic connection is rare and precious. Nurture it with honesty and vulnerability.`
          : score >= 70
          ? `Focus on understanding each other's love languages and emotional needs.`
          : `Success requires patience, empathy, and willingness to learn from each other.`;
      } else if (relationshipType.includes('Business') || relationshipType.includes('Professional') || relationshipType.includes('Creative')) {
        advice = score >= 85
          ? `Your combined energies create powerful synergy for professional success.`
          : score >= 70
          ? `Establish clear communication channels and respect different working styles.`
          : `Define roles clearly and focus on complementary strengths rather than differences.`;
      } else if (relationshipType.includes('Family') || relationshipType.includes('Sibling') || relationshipType.includes('Parent')) {
        advice = score >= 85
          ? `Your family bond is blessed with cosmic harmony. Cherish and protect it.`
          : score >= 70
          ? `Practice patience and understanding generational or personality differences.`
          : `Family bonds require extra effort - focus on love and acceptance.`;
      } else if (relationshipType.includes('Friendship')) {
        advice = score >= 85
          ? `You've found a truly kindred spirit. This friendship will enrich both your lives.`
          : score >= 70
          ? `Your friendship has great potential - nurture it with regular communication.`
          : `Different perspectives can strengthen friendship if approached with openness.`;
      } else {
        advice = score >= 85
          ? `Focus on maintaining your individual growth while nurturing your beautiful connection.`
          : score >= 70
          ? `Practice open communication and mutual respect to strengthen your bond.`
          : `Invest time in understanding each other's perspectives and finding common ground.`;
      }

      let relationshipInsight = '';
      if (relationshipType.includes('Love') || relationshipType.includes('Romance')) {
        relationshipInsight = `In matters of the heart, ${yourSign} brings ${element1.toLowerCase()} passion while ${partnerSign} contributes ${element2.toLowerCase()} depth.`;
      } else if (relationshipType.includes('Business')) {
        relationshipInsight = `Professionally, ${yourSign}'s ${element1.toLowerCase()} drive complements ${partnerSign}'s ${element2.toLowerCase()} approach.`;
      } else if (relationshipType.includes('Friendship')) {
        relationshipInsight = `As friends, ${yourSign}'s ${element1.toLowerCase()} energy balances ${partnerSign}'s ${element2.toLowerCase()} nature.`;
      }

      setCompatibilityResult({
        success: true,
        score,
        analysis: analysis + (relationshipInsight ? ` ${relationshipInsight}` : ''),
        strengths,
        challenges,
        advice,
        your_sign: yourSign,
        partner_sign: partnerSign,
        relationship_type: relationshipType,
        your_name: yourName || 'You',
        partner_name: partnerName || 'Your Partner',
        use_advanced_mode: useAdvancedMode
      });

    } catch (err: any) {
      console.error('Compatibility Error:', err);
      setError(err.message || 'Error calculating compatibility. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getCompatibilityScore = (sign1: string, sign2: string) => {
    const elements: Record<string, string> = {
      'Aries': 'Fire', 'Leo': 'Fire', 'Sagittarius': 'Fire',
      'Taurus': 'Earth', 'Virgo': 'Earth', 'Capricorn': 'Earth',
      'Gemini': 'Air', 'Libra': 'Air', 'Aquarius': 'Air',
      'Cancer': 'Water', 'Scorpio': 'Water', 'Pisces': 'Water'
    };

    const element1 = elements[sign1];
    const element2 = elements[sign2];

    if (element1 === element2) return 85;
    if ((element1 === 'Fire' && element2 === 'Air') || (element1 === 'Air' && element2 === 'Fire')) return 90;
    if ((element1 === 'Earth' && element2 === 'Water') || (element1 === 'Water' && element2 === 'Earth')) return 88;
    if ((element1 === 'Fire' && element2 === 'Water') || (element1 === 'Water' && element2 === 'Fire')) return 65;
    if ((element1 === 'Earth' && element2 === 'Air') || (element1 === 'Air' && element2 === 'Earth')) return 70;
    return 75;
  };

  const getCompatibilityColor = (score: number) => {
    if (score >= 85) return '#28a745';
    if (score >= 70) return '#ffc107';
    return '#dc3545';
  };

  return (
    <div className="compatibility-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <h1 className="hero-title">Discover Your Cosmic Connection</h1>
          <p className="hero-subtitle">Find out how compatible you are with your partner, friends, or colleagues</p>
          <div className="hero-ornament">
            <span className="ornament-symbol">💕</span>
            <span className="ornament-line"></span>
            <span className="ornament-symbol">⭐</span>
          </div>
        </div>
      </section>

      {/* Compatibility Checker */}
      <section className="compatibility-checker">
        <div className="section-container">
          <h2 className="section-title">Check Your Compatibility</h2>
          <p className="section-subtitle">Enter your details to discover your cosmic connection</p>
          
          <div className="compatibility-form">
            {/* Mode Toggle */}
            <div className="mode-toggle">
              <label className="toggle-label">
                <input
                  type="checkbox"
                  checked={useAdvancedMode}
                  onChange={(e) => setUseAdvancedMode(e.target.checked)}
                  className="toggle-input"
                />
                <span className="toggle-slider"></span>
                <span className="toggle-text">Advanced Mode (Birth Details)</span>
              </label>
            </div>

            {/* FIX 1: Wrapped basic mode content in a Fragment so the optional-details
                       block is correctly inside the ternary's true branch */}
            {!useAdvancedMode ? (
              <>
                <div className="form-grid">
                  <div className="form-group">
                    {/* FIX 2: Unique IDs for basic mode inputs to avoid duplicate-id errors */}
                    <label htmlFor="basic-yourSign">Your Zodiac Sign</label>
                    <select
                      id="basic-yourSign"
                      value={yourSign}
                      onChange={(e) => setYourSign(e.target.value)}
                      className="form-select"
                    >
                      <option value="">Select your zodiac sign</option>
                      {zodiacSigns.map((sign) => (
                        <option key={sign} value={sign}>{sign}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="basic-partnerSign">Partner's Zodiac Sign</label>
                    <select
                      id="basic-partnerSign"
                      value={partnerSign}
                      onChange={(e) => setPartnerSign(e.target.value)}
                      className="form-select"
                    >
                      <option value="">Select partner's zodiac sign</option>
                      {zodiacSigns.map((sign) => (
                        <option key={sign} value={sign}>{sign}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="basic-relationshipType">Relationship Type</label>
                    <select
                      id="basic-relationshipType"
                      value={relationshipType}
                      onChange={(e) => setRelationshipType(e.target.value)}
                      className="form-select"
                    >
                      <option value="">Select relationship type</option>
                      {relationshipTypes.map((type) => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Optional Birth Details — now correctly inside the ternary true branch */}
                <div className="optional-details">
                  <h4>📍 Add Birth Details for Enhanced Accuracy (Optional)</h4>
                  <div className="form-grid">
                    <div className="form-group">
                      <label htmlFor="basic-yourBirthTime">Your Birth Time</label>
                      <input
                        type="time"
                        id="basic-yourBirthTime"
                        value={yourBirthTime}
                        onChange={(e) => setYourBirthTime(e.target.value)}
                        placeholder="HH:MM"
                        className="form-input"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="basic-yourBirthCity">Your Birth City</label>
                      <select
                        id="basic-yourBirthCity"
                        value={yourBirthCity}
                        onChange={(e) => setYourBirthCity(e.target.value)}
                        className="form-select"
                      >
                        <option value="">Select your birth city</option>
                        {cities.map((cityName) => (
                          <option key={cityName} value={cityName}>{cityName}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="basic-partnerBirthTime">Partner's Birth Time</label>
                      <input
                        type="time"
                        id="basic-partnerBirthTime"
                        value={partnerBirthTime}
                        onChange={(e) => setPartnerBirthTime(e.target.value)}
                        placeholder="HH:MM"
                        className="form-input"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="basic-partnerBirthCity">Partner's Birth City</label>
                      <select
                        id="basic-partnerBirthCity"
                        value={partnerBirthCity}
                        onChange={(e) => setPartnerBirthCity(e.target.value)}
                        className="form-select"
                      >
                        <option value="">Select partner's birth city</option>
                        {cities.map((cityName) => (
                          <option key={cityName} value={cityName}>{cityName}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              /* Advanced Mode - Detailed Form */
              <div className="advanced-form">
                <div className="person-section">
                  <h4>Your Details</h4>
                  <div className="form-grid">
                    <div className="form-group">
                      <label htmlFor="adv-yourName">Your Name</label>
                      <input
                        type="text"
                        id="adv-yourName"
                        value={yourName}
                        onChange={(e) => setYourName(e.target.value)}
                        placeholder="Enter your name"
                        className="form-input"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="adv-yourSign">Your Zodiac Sign</label>
                      <select
                        id="adv-yourSign"
                        value={yourSign}
                        onChange={(e) => setYourSign(e.target.value)}
                        className="form-select"
                      >
                        <option value="">Select your zodiac sign</option>
                        {zodiacSigns.map((sign) => (
                          <option key={sign} value={sign}>{sign}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="adv-yourBirthDate">Your Birth Date</label>
                      <input
                        type="date"
                        id="adv-yourBirthDate"
                        value={yourBirthDate}
                        onChange={(e) => setYourBirthDate(e.target.value)}
                        className="form-input"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="adv-yourBirthTime">Your Birth Time</label>
                      <input
                        type="time"
                        id="adv-yourBirthTime"
                        value={yourBirthTime}
                        onChange={(e) => setYourBirthTime(e.target.value)}
                        className="form-input"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="adv-yourBirthCity">Your Birth City</label>
                      <select
                        id="adv-yourBirthCity"
                        value={yourBirthCity}
                        onChange={(e) => setYourBirthCity(e.target.value)}
                        className="form-select"
                      >
                        <option value="">Select your birth city</option>
                        {cities.map((cityName) => (
                          <option key={cityName} value={cityName}>{cityName}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="person-section">
                  <h4>Partner's Details</h4>
                  <div className="form-grid">
                    <div className="form-group">
                      <label htmlFor="adv-partnerName">Partner's Name</label>
                      <input
                        type="text"
                        id="adv-partnerName"
                        value={partnerName}
                        onChange={(e) => setPartnerName(e.target.value)}
                        placeholder="Enter partner's name"
                        className="form-input"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="adv-partnerSign">Partner's Zodiac Sign</label>
                      <select
                        id="adv-partnerSign"
                        value={partnerSign}
                        onChange={(e) => setPartnerSign(e.target.value)}
                        className="form-select"
                      >
                        <option value="">Select partner's zodiac sign</option>
                        {zodiacSigns.map((sign) => (
                          <option key={sign} value={sign}>{sign}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="adv-partnerBirthDate">Partner's Birth Date</label>
                      <input
                        type="date"
                        id="adv-partnerBirthDate"
                        value={partnerBirthDate}
                        onChange={(e) => setPartnerBirthDate(e.target.value)}
                        className="form-input"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="adv-partnerBirthTime">Partner's Birth Time</label>
                      <input
                        type="time"
                        id="adv-partnerBirthTime"
                        value={partnerBirthTime}
                        onChange={(e) => setPartnerBirthTime(e.target.value)}
                        className="form-input"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="adv-partnerBirthCity">Partner's Birth City</label>
                      <select
                        id="adv-partnerBirthCity"
                        value={partnerBirthCity}
                        onChange={(e) => setPartnerBirthCity(e.target.value)}
                        className="form-select"
                      >
                        <option value="">Select partner's birth city</option>
                        {cities.map((cityName) => (
                          <option key={cityName} value={cityName}>{cityName}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="adv-relationshipType">Relationship Type</label>
                  <select
                    id="adv-relationshipType"
                    value={relationshipType}
                    onChange={(e) => setRelationshipType(e.target.value)}
                    className="form-select"
                  >
                    <option value="">Select relationship type</option>
                    {relationshipTypes.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}
            
            <button 
              onClick={handleCheckCompatibility} 
              className="check-btn"
              disabled={loading}
            >
              {loading ? 'Checking...' : 'Check Compatibility'}
            </button>
          </div>

          {error && <div className="error-message">{error}</div>}

          {compatibilityResult && (
            <div className="compatibility-result">
              <h3>Compatibility Analysis for {compatibilityResult.your_name} & {compatibilityResult.partner_name}</h3>
              
              <div className="compatibility-score">
                <div className="score-circle">
                  <span className="score-number">
                    {compatibilityResult.score || getCompatibilityScore(yourSign, partnerSign)}%
                  </span>
                </div>
                <div className="score-label">Compatibility Score</div>
                <div className="relationship-type">{compatibilityResult.relationship_type}</div>
              </div>

              <div className="compatibility-summary">
                <div className="summary-text">
                  <p><strong>{compatibilityResult.your_name}</strong> ({compatibilityResult.your_sign}) and <strong>{compatibilityResult.partner_name}</strong> ({compatibilityResult.partner_sign})</p>
                  <p>{compatibilityResult.analysis}</p>
                </div>
              </div>

              <div className="compatibility-details">
                <div className="detail-section">
                  <h4>💪 Strengths</h4>
                  <p>{compatibilityResult.strengths || 'You share complementary energies that bring balance and harmony to your relationship.'}</p>
                </div>
                
                <div className="detail-section">
                  <h4>⚠️ Challenges</h4>
                  <p>{compatibilityResult.challenges || 'Different approaches may require understanding and compromise to maintain harmony.'}</p>
                </div>
                
                <div className="detail-section">
                  <h4>💡 Advice</h4>
                  <p>{compatibilityResult.advice || 'Focus on open communication and mutual respect to strengthen your bond.'}</p>
                </div>
              </div>

              {compatibilityResult.use_advanced_mode && (
                <div className="advanced-insights">
                  <h4>🔮 Advanced Insights</h4>
                  <div className="insights-grid">
                    <div className="insight-item">
                      <h5>Birth Chart Alignment</h5>
                      <p>Your birth details suggest deeper cosmic connections beyond sun signs.</p>
                    </div>
                    <div className="insight-item">
                      <h5>Elemental Harmony</h5>
                      <p>The combination of your birth elements creates unique relationship dynamics.</p>
                    </div>
                    <div className="insight-item">
                      <h5>Timing & Destiny</h5>
                      <p>Your birth times indicate specific karmic connections and life lessons together.</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Zodiac Compatibility Grid */}
      <section className="zodiac-grid-section">
        <div className="section-container">
          <h2 className="section-title">Zodiac Compatibility Guide</h2>
          <div className="zodiac-compatibility-grid">
            {zodiacSigns.map((sign) => (
              <div key={sign} className="zodiac-compatibility-card">
                <div className="zodiac-symbol">
                  {sign === 'Aries' && '♈'}
                  {sign === 'Taurus' && '♉'}
                  {sign === 'Gemini' && '♊'}
                  {sign === 'Cancer' && '♋'}
                  {sign === 'Leo' && '♌'}
                  {sign === 'Virgo' && '♍'}
                  {sign === 'Libra' && '♎'}
                  {sign === 'Scorpio' && '♏'}
                  {sign === 'Sagittarius' && '♐'}
                  {sign === 'Capricorn' && '♑'}
                  {sign === 'Aquarius' && '♒'}
                  {sign === 'Pisces' && '♓'}
                </div>
                <h3 className="zodiac-name">{sign}</h3>
                <div className="best-matches">
                  <span className="match-label">Best Matches:</span>
                  <div className="match-signs">
                    {sign === 'Aries' && ['Leo', 'Sagittarius', 'Gemini'].map(match => (
                      <span key={match} className="match-sign">{match}</span>
                    ))}
                    {sign === 'Taurus' && ['Virgo', 'Capricorn', 'Cancer'].map(match => (
                      <span key={match} className="match-sign">{match}</span>
                    ))}
                    {sign === 'Gemini' && ['Libra', 'Aquarius', 'Aries'].map(match => (
                      <span key={match} className="match-sign">{match}</span>
                    ))}
                    {sign === 'Cancer' && ['Scorpio', 'Pisces', 'Taurus'].map(match => (
                      <span key={match} className="match-sign">{match}</span>
                    ))}
                    {sign === 'Leo' && ['Aries', 'Sagittarius', 'Gemini'].map(match => (
                      <span key={match} className="match-sign">{match}</span>
                    ))}
                    {sign === 'Virgo' && ['Taurus', 'Capricorn', 'Scorpio'].map(match => (
                      <span key={match} className="match-sign">{match}</span>
                    ))}
                    {sign === 'Libra' && ['Gemini', 'Aquarius', 'Leo'].map(match => (
                      <span key={match} className="match-sign">{match}</span>
                    ))}
                    {sign === 'Scorpio' && ['Cancer', 'Pisces', 'Virgo'].map(match => (
                      <span key={match} className="match-sign">{match}</span>
                    ))}
                    {sign === 'Sagittarius' && ['Aries', 'Leo', 'Libra'].map(match => (
                      <span key={match} className="match-sign">{match}</span>
                    ))}
                    {sign === 'Capricorn' && ['Taurus', 'Virgo', 'Scorpio'].map(match => (
                      <span key={match} className="match-sign">{match}</span>
                    ))}
                    {sign === 'Aquarius' && ['Gemini', 'Libra', 'Sagittarius'].map(match => (
                      <span key={match} className="match-sign">{match}</span>
                    ))}
                    {sign === 'Pisces' && ['Cancer', 'Scorpio', 'Taurus'].map(match => (
                      <span key={match} className="match-sign">{match}</span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Compatibility;